import React, { useState, useRef } from 'react';
import './Contact.css';

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [status, setStatus] = useState('');
  const messageRef = useRef(null);

  const colors = [
    '#007bff', // Blue
    '#dc3545', // Red
    '#28a745', // Green
    '#ffc107', // Yellow
    '#6610f2', // Purple
    '#fd7e14', // Orange
    '#20c997', // Teal
    '#e83e8c', // Pink
    '#000000', // Black
    '#6c757d'  // Gray
  ];

  const handleInputFocus = (e) => {
    e.target.parentElement.classList.add('ws-contact-focused');
  };

  const handleInputBlur = (e) => {
    if (!e.target.value) {
      e.target.parentElement.classList.remove('ws-contact-focused');
    }
  };

  const handleEditorClick = (e) => {
    // Ensure the editor is focused and ready for input
    if (messageRef.current) {
      // Focus the editor
      messageRef.current.focus();
      
      // If the editor is empty or only has a BR tag, set up proper cursor
      const content = messageRef.current.innerHTML.trim();
      if (content === '' || content === '<br>' || content === '<br/>') {
        e.preventDefault();
        
        // Clear any existing content
        messageRef.current.innerHTML = '';
        
        // Create a range and selection for cursor placement
        const selection = window.getSelection();
        const range = document.createRange();
        
        // Select the editor's content (which is now empty)
        range.selectNodeContents(messageRef.current);
        range.collapse(false); // Collapse to end (which is also the start when empty)
        
        // Apply the selection
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleEditorFocus = () => {
    // When editor gets focus, ensure cursor is properly placed
    if (messageRef.current) {
      setTimeout(() => {
        const content = messageRef.current.innerHTML.trim();
        if (content === '' || content === '<br>' || content === '<br/>') {
          messageRef.current.innerHTML = '';
          
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(messageRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 0);
    }
  };

  const handleEditorBlur = () => {
    // Editor lost focus
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleMessageChange = () => {
    if (messageRef.current) {
      setFormState(prevState => ({
        ...prevState,
        message: messageRef.current.innerHTML
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setStatus('submitting');

    try {
      // Validate that we have content to send
      if (!messageRef.current) {
        throw new Error('Message editor not initialized');
      }

      // Function to convert HTML to plain text
      const stripHtml = (html) => {
        // Create a temporary div
        const temp = document.createElement('div');
        temp.innerHTML = html;
        
        // Function to process a node and its children
        const processNode = (node) => {
          let text = '';
          
          if (node.nodeType === Node.TEXT_NODE) {
            // For text nodes, just get the text
            text += node.textContent;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            // For elements, handle special cases
            if (node.tagName === 'DIV' || node.tagName === 'P' || node.tagName === 'BR') {
              // Add newline for block elements
              text += '\n';
            } else if (node.tagName === 'LI') {
              // Add bullet point for list items
              text += '• ';
            }
            
            // Process all child nodes
            Array.from(node.childNodes).forEach(child => {
              text += processNode(child);
            });
            
            // Add extra newline after lists
            if (node.tagName === 'UL' || node.tagName === 'OL') {
              text += '\n';
            }
          }
          
          return text;
        };
        
        // Get the text and clean it up
        let text = processNode(temp);
        
        // Clean up extra whitespace and newlines
        text = text.replace(/\n\s*\n\s*\n/g, '\n\n')  // Replace multiple newlines with max two
                   .replace(/^\s+|\s+$/g, '')         // Trim start and end
                   .replace(/[ \t]+/g, ' ');          // Replace multiple spaces with single space
        
        return text;
      };

      const messageContent = stripHtml(messageRef.current.innerHTML);
      
      // Basic form validation
      if (!messageContent.trim()) {
        throw new Error('Message cannot be empty');
      }
      if (!formState.name.trim() || !formState.email.trim()) {
        throw new Error('Name and email are required');
      }

      const response = await fetch('https://formspree.io/f/myzndzdj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formState.name.trim(),
          email: formState.email.trim(),
          message: messageContent // Send plain text content
        })
      });

      const responseData = await response.json();

      if (response.ok) {
        setStatus('success');
        // Reset form
        setFormState({
          name: '',
          email: '',
          message: ''
        });
        if (messageRef.current) {
          messageRef.current.innerHTML = '';
        }
        // Remove focused states
        document.querySelectorAll('.ws-contact-focused').forEach(el => {
          el.classList.remove('ws-contact-focused');
        });
      } else {
        console.error('Form submission failed:', responseData);
        throw new Error(responseData.error || 'Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
      // Show more specific error message if available
      const errorMessage = document.querySelector('.ws-contact-error-message p');
      if (errorMessage) {
        errorMessage.textContent = error.message || 'Oops! Something went wrong. Please try again later.';
      }
    }
  };


  const applyFormatting = (command, value = null) => {
    if (messageRef.current) {
      // Ensure the editor has focus
      messageRef.current.focus();
      
      try {
        if (command === 'color') {
          // Get the selection
          const selection = window.getSelection();
          const range = selection.getRangeAt(0);
          
          // Create a new span element with the color
          const span = document.createElement('span');
          span.style.color = value;
          
          // Wrap the selected text in the colored span
          range.surroundContents(span);
          
          // Hide color picker after applying color
          setShowColorPicker(false);
        } else {
          // For all other commands, use execCommand
          document.execCommand(command, false, value);
        }
        
        // Force update after formatting
        handleMessageChange();
      } catch (error) {
        console.error('Error applying formatting:', error);
      }
    }
  };

  // Close color picker when clicking outside
  const handleClickOutside = (e) => {
    // Don't interfere with the message editor
    if (e.target.closest('.ws-contact-message-editor')) {
      return;
    }
    if (!e.target.closest('.ws-color-picker') && !e.target.closest('.ws-format-btn-color')) {
      setShowColorPicker(false);
    }
  };

  // Add click outside listener
  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <section className="ws-contact-section" id="contact">
      <div className="ws-contact-container">
        <div className="ws-contact-content">
          {/* Header Section */}
          <div className="ws-contact-header">
            <span className="ws-contact-section-subtitle">Contact Us</span>
            <h2 className="ws-contact-section-title">
              Let's Create Something
              <span className="ws-contact-highlight"> Amazing Together</span>
            </h2>
          </div>

          {/* Main Content - Cards and Form */}
          <div className="ws-contact-main-content">
            {/* Contact Cards Section */}
            <div className="ws-contact-cards-section">
              <div className="ws-contact-cards">
                <div className="ws-contact-card">
                  <div className="ws-contact-card-icon">
                    <i className="fas fa-paper-plane"></i>
                  </div>
                  <div className="ws-contact-card-content">
                    <h3>Ready to Get Started?</h3>
                    <p className="ws-contact-subtitle">Drop us a message and we'll get back to you within 24 hours</p>
                    <div className="ws-contact-emails">
                      <div className="ws-email-item">
                        <i className="fas fa-envelope"></i>
                        <a href="mailto:info@projectofi.com" className="ws-email-protected" data-email="info">
                          <span className="ws-email-text">Contact us via email</span>
                        </a>
                      </div>
                      <div className="ws-email-item">
                        <i className="fas fa-headset"></i>
                        <a href="mailto:support@projectofi.com" className="ws-email-protected" data-email="support">
                          <span className="ws-email-text">Get support</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form Section */}
            <div className="ws-contact-form-section">
              <div className="ws-contact-form-wrapper">
                {status === 'success' ? (
                  <div className="ws-contact-success-message">
                    <i className="fas fa-check-circle"></i>
                    <h3>Thank you for your message!</h3>
                    <p>We'll get back to you as soon as possible.</p>
                    <button 
                      className="ws-contact-reset-button"
                      onClick={() => setStatus('')}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form className="ws-contact-form" onSubmit={handleSubmit}>
                    <div className="ws-contact-form-group">
                      <input
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder=" "
                        required
                      />
                      <label>Your Name</label>
                    </div>

                    <div className="ws-contact-form-group">
                      <input
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder=" "
                        required
                      />
                      <label>Your Email</label>
                    </div>

                    <div className="ws-contact-form-group">
                      <div className="ws-formatting-toolbar">
                        {/* Text Style Buttons */}
                        <div className="ws-format-group">
                          <button 
                            type="button" 
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyFormatting('bold');
                            }} 
                            className="ws-format-btn"
                            title="Bold"
                          >
                            <i className="fas fa-bold"></i>
                          </button>
                          <button 
                            type="button" 
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyFormatting('italic');
                            }} 
                            className="ws-format-btn"
                            title="Italic"
                          >
                            <i className="fas fa-italic"></i>
                          </button>
                          <div className="ws-color-picker-container">
                            <button 
                              type="button" 
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setShowColorPicker(!showColorPicker);
                              }} 
                              className="ws-format-btn ws-format-btn-color"
                              title="Text Color"
                            >
                              <i className="fas fa-palette"></i>
                            </button>
                            {showColorPicker && (
                              <div className="ws-color-picker">
                                {colors.map((color, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    className="ws-color-option"
                                    style={{ backgroundColor: color }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      applyFormatting('color', color);
                                    }}
                                    title={color}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="ws-format-divider"></div>

                        {/* Text Alignment Buttons */}
                        <div className="ws-format-group">
                          <button 
                            type="button" 
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyFormatting('justifyLeft');
                            }} 
                            className="ws-format-btn"
                            title="Align Left"
                          >
                            <i className="fas fa-align-left"></i>
                          </button>
                          <button 
                            type="button" 
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyFormatting('justifyCenter');
                            }} 
                            className="ws-format-btn"
                            title="Align Center"
                          >
                            <i className="fas fa-align-center"></i>
                          </button>
                          <button 
                            type="button" 
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyFormatting('justifyRight');
                            }} 
                            className="ws-format-btn"
                            title="Align Right"
                          >
                            <i className="fas fa-align-right"></i>
                          </button>
                        </div>

                        {/* Divider */}
                        <div className="ws-format-divider"></div>

                        {/* List Buttons */}
                        <div className="ws-format-group">
                          <button 
                            type="button" 
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyFormatting('insertUnorderedList');
                            }} 
                            className="ws-format-btn"
                            title="Bullet List"
                          >
                            <i className="fas fa-list-ul"></i>
                          </button>
                          <button 
                            type="button" 
                            onMouseDown={(e) => {
                              e.preventDefault();
                              applyFormatting('insertOrderedList');
                            }} 
                            className="ws-format-btn"
                            title="Numbered List"
                          >
                            <i className="fas fa-list-ol"></i>
                          </button>
                        </div>
                      </div>
                      <div
                        ref={messageRef}
                        className="ws-contact-message-editor"
                        contentEditable="true"
                        onInput={handleMessageChange}
                        onClick={handleEditorClick}
                        onFocus={handleEditorFocus}
                        onBlur={handleEditorBlur}
                        data-placeholder="Your Message"
                        suppressContentEditableWarning={true}
                        role="textbox"
                        aria-multiline="true"
                      ></div>
                    </div>

                    {status === 'error' && (
                      <div className="ws-contact-error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        <p>Oops! Something went wrong. Please try again later.</p>
                      </div>
                    )}

                    <button 
                      type="submit" 
                      className="ws-contact-submit-button"
                      disabled={status === 'submitting'}
                    >
                      {status === 'submitting' ? (
                        <>
                          <span>Sending...</span>
                          <i className="fas fa-spinner fa-spin"></i>
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <i className="fas fa-paper-plane"></i>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
